// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { AppIcon, Button, ButtonGroup, IconButton, Modal, Spinner, Tooltip, useToast } from '@loa/ui-components';
import { clientApi } from '../../../api/clientApi';
import { proposalApi } from '../../../api/proposalApi';
import { quoteApi } from '../../../api/quoteApi';
import { relationshipApi } from '../../../api/relationshipApi';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import type { Client } from '../../../types/client';
import type { Quote } from '../../../types/quote';
import type {
  PensionWizardBootstrapResult,
  Proposal,
  ProposalSection,
} from '../../../types/proposal';
import { ADDABLE_PROPOSAL_SECTION_KEYS, SHARE_OPTIONAL_SECTION_KEYS } from '../../../types/proposal';
import { getQuoteBackedBuilderConfig, type QuoteBackedProposalType } from './builderConfigs';
import { WizardStepQuoteRequest } from '../wizard/WizardStepQuoteRequest';
import { InvestmentQuoteRequestStep } from './InvestmentQuoteRequestStep';
import { ProtectionQuoteRequestStep } from './ProtectionQuoteRequestStep';
import { WizardStepQuoteOptions } from '../wizard/WizardStepQuoteOptions';
import { QuoteBackedClientPreview } from './QuoteBackedClientPreview';
import { renderQuoteBackedSection } from './quoteBackedSectionRenderer';
// The reused wizard quote-request step styles (pension-wizard-step__*) live
// in the wizard's stylesheet; shared by the Investment/Protection steps too.
import '../wizard/PensionProposalWizard.css';
import './PensionProposalBuilder.css';

/** Human-readable labels for the "+" chooser (PRD §3.2 rail add-section). */
const ADDABLE_SECTION_LABELS: Record<string, string> = {
  APPENDICES: 'Appendices',
};

/** Advisor's editable builder vs. the client's read-only document preview (mirrors BreakthroughReportBuilder's toggle). */
type BuilderViewMode = 'advisor' | 'client';

// -- Synthetic (client-side) rail sections ----------------------------------
// The quote steps of the v1 wizard live INSIDE the builder rail for
// builder-enabled brokerages — they are not backend proposal_sections.

const QUOTE_REQUEST_KEY = 'QUOTE_REQUEST';
const QUOTE_OPTIONS_KEY = 'QUOTE_OPTIONS';

type RailEntry = Readonly<{
  key: string;
  title: string;
  complete: boolean;
  locked: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

/**
 * Pull the incomplete section keys out of the backend's
 * `SECTIONS_INCOMPLETE: ...` 422 message — tokens that match a known
 * section key are treated as the offending sections.
 */
function extractIncompleteSectionKeys(message: string, sections: ProposalSection[]): string[] {
  const known = new Set(sections.map((s) => s.sectionKey));
  const tokens = message.split(/[^A-Z_]+/).filter(Boolean);
  return [...new Set(tokens.filter((t) => known.has(t)))];
}

export type QuoteBackedProposalBuilderProps = Readonly<{
  proposalType: QuoteBackedProposalType;
}>;

/**
 * Generalised sectioned proposal builder — one shell serving PENSION,
 * FUND_INVESTMENT and PROTECTION (docs/specs/investment-protection-proposals-prd.md
 * RQ-8, generalising `PensionProposalBuilder` per the pension-proposal-builder-v2
 * PRD §4). Left "Sections" rail with completion ticks and an add-section
 * chooser, and a right content pane that switches on the active section's
 * key. Behaviour is driven entirely by the per-type
 * {@link getQuoteBackedBuilderConfig} config: the recommendation section key,
 * the "new mode" placeholder titles, and the `data-testid` prefix. The CSS
 * class prefix (`pension-builder__*`) intentionally stays literal across all
 * three types — it is shared generic builder chrome, not a per-type
 * identifier.
 *
 * The v1 wizard's quote steps are hosted as the rail's first two synthetic
 * sections — QUOTE_REQUEST (type-dispatched to
 * {@link WizardStepQuoteRequest} for PENSION or the Investment/Protection
 * quote-request steps otherwise) and QUOTE_OPTIONS (the type-agnostic
 * {@link WizardStepQuoteOptions}) — so builder-enabled brokerages get one
 * continuous flow from quote request to Share With Client.
 *
 * Routes:
 * - `/relationships/:profileId/proposals/{pension,investment,protection}-builder`
 *   — "new" mode: only Quote Request is unlocked; submitting it bootstraps
 *   the quote + DRAFT proposal and replaces the URL with the id route.
 * - `…/{...}-builder/:proposalId` — normal mode over an existing
 *   (builder-born) proposal.
 */
export function QuoteBackedProposalBuilder({ proposalType }: QuoteBackedProposalBuilderProps) {
  const config = getQuoteBackedBuilderConfig(proposalType);
  const { profileId, proposalId } = useParams<{ profileId: string; proposalId?: string }>();
  const navigate = useNavigate();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [sections, setSections] = useState<ProposalSection[]>([]);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [brokerageId, setBrokerageId] = useState('');
  // null while the resume default (quote selected? first incomplete section)
  // hasn't been derived yet; "new" mode always starts on Quote Request.
  const [activeKey, setActiveKey] = useState<string | null>(
    proposalId ? null : QUOTE_REQUEST_KEY,
  );
  const [optionsRefreshKey, setOptionsRefreshKey] = useState(0);
  const [loading, setLoading] = useState(Boolean(proposalId));
  const [error, setError] = useState<string | null>(null);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [addingKey, setAddingKey] = useState<string | null>(null);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [viewMode, setViewMode] = useState<BuilderViewMode>('advisor');
  const { showToast } = useToast();

  // Relationship (profile) display name for the breadcrumb trail; the same
  // fetch supplies the brokerageId the quote-request step needs for its
  // provider list (mirrors PensionProposalWizard).
  const [relationshipName, setRelationshipName] = useState<string | null>(null);

  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipApi
      .getRelationship(profileId)
      .then((r) => {
        if (cancelled) return;
        setRelationshipName(r.relationshipName || r.profileName || null);
        setBrokerageId(r.brokerageId);
      })
      .catch(() => {
        if (!cancelled) setRelationshipName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // Clients back the quote-request step's "Who is the quote for?" radio —
  // same source as the wizard shell.
  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    clientApi
      .getClientsByProfile(profileId)
      .then((list) => {
        if (!cancelled) setClients(list);
      })
      .catch(() => {
        // Non-fatal — the client radio just renders empty and the advisor
        // can retry by reloading.
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  useEffect(() => {
    setBreadcrumbItems([
      { label: 'Relationships', to: '/relationships' },
      ...(profileId && relationshipName
        ? [{ label: relationshipName, to: `/relationships/${profileId}` }]
        : []),
      {
        label: 'Proposals',
        to: profileId ? `/relationships/${profileId}?tab=proposals` : undefined,
      },
    ]);
    setBackTo(profileId ? `/relationships/${profileId}?tab=proposals` : null);
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [profileId, relationshipName, setBreadcrumbItems, setBackTo]);

  useEffect(() => {
    if (!proposalId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([proposalApi.getProposal(proposalId), proposalApi.getSections(proposalId)])
      .then(async ([loadedProposal, loadedSections]) => {
        // The linked quote drives the QUOTE_OPTIONS tick + the resume
        // default; a failure here is non-fatal (the tick just stays off).
        let loadedQuote: Quote | null = null;
        if (loadedProposal.sourceQuoteId) {
          try {
            loadedQuote = await quoteApi.getQuote(loadedProposal.sourceQuoteId);
          } catch {
            loadedQuote = null;
          }
        }
        if (cancelled) return;
        setProposal(loadedProposal);
        setSections(loadedSections);
        setQuote(loadedQuote);
        // Resume default (only when nothing is already active — the
        // bootstrap flow pre-selects QUOTE_OPTIONS before the URL swap):
        // no selected option yet → Quote Options; otherwise the first
        // incomplete backend section (or About You).
        setActiveKey(
          (prev) =>
            prev ??
            (loadedQuote && !loadedQuote.selectedOptionId
              ? QUOTE_OPTIONS_KEY
              : loadedSections.find((s) => !s.complete)?.sectionKey ??
                loadedSections[0]?.sectionKey ??
                'ABOUT_YOU'),
        );
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load this proposal');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [proposalId]);

  const refreshSections = useCallback(async () => {
    if (!proposalId) return;
    try {
      setSections(await proposalApi.getSections(proposalId));
    } catch {
      // Best-effort — the rail keeps its last-known completion state.
    }
  }, [proposalId]);

  const refreshQuote = useCallback(async () => {
    const quoteId = proposal?.sourceQuoteId;
    if (!quoteId) return;
    try {
      setQuote(await quoteApi.getQuote(quoteId));
    } catch {
      // Best-effort — the tick keeps its last-known state.
    }
  }, [proposal?.sourceQuoteId]);

  const handleSectionSaved = useCallback((updated: ProposalSection) => {
    setSections((prev) => prev.map((s) => (s.sectionKey === updated.sectionKey ? updated : s)));
  }, []);

  const handleSectionRemoved = useCallback((sectionKey: string) => {
    setSections((prev) => prev.filter((s) => s.sectionKey !== sectionKey));
    setActiveKey((prev) => {
      if (prev !== sectionKey) return prev;
      const remaining = sections.filter((s) => s.sectionKey !== sectionKey);
      return remaining[0]?.sectionKey ?? null;
    });
  }, [sections]);

  // -- Quote step wiring (synthetic rail sections) -------------------------

  /**
   * New-mode bootstrap: the quote + DRAFT proposal now exist. Land on Quote
   * Options and swap the URL to the id route (replace, so Back doesn't
   * return to the blank "new" page). The id-route load effect then pulls the
   * backend sections + quote.
   */
  const handleBootstrapped = useCallback(
    (result: PensionWizardBootstrapResult) => {
      setProposal(result.proposal);
      setActiveKey(QUOTE_OPTIONS_KEY);
      navigate(
        `/relationships/${profileId}/proposals/${config.routeSegment}/${result.proposalId}`,
        { replace: true },
      );
    },
    [navigate, profileId, config.routeSegment],
  );

  /** Edit round-trip complete — show the (re-generating) options again. */
  const handleQuoteUpdated = useCallback((updated: Quote) => {
    setQuote(updated);
    setOptionsRefreshKey((k) => k + 1);
    setActiveKey(QUOTE_OPTIONS_KEY);
  }, []);

  const handleQuoteRequestCancel = useCallback(() => {
    if (proposal) {
      // Cancelling an edit just returns to the options section unchanged.
      setActiveKey(QUOTE_OPTIONS_KEY);
      return;
    }
    navigate(profileId ? `/relationships/${profileId}?tab=proposals` : '/relationships');
  }, [proposal, navigate, profileId]);

  const handleEditRequested = useCallback((q: Quote) => {
    setQuote(q);
    setActiveKey(QUOTE_REQUEST_KEY);
  }, []);

  const handleSaveAndClose = useCallback(() => {
    navigate(profileId ? `/relationships/${profileId}?tab=proposals` : '/relationships');
  }, [navigate, profileId]);

  /**
   * "Continue to Builder" from Quote Options: an option is now selected, so
   * the recommendation figures are derivable — refetch the backend sections
   * and move into the document sections.
   */
  const handleContinueToBuilder = useCallback(
    (q: Quote) => {
      setQuote(q);
      void refreshSections();
      setActiveKey('ABOUT_YOU');
    },
    [refreshSections],
  );

  const handleQuoteChanged = useCallback((q: Quote) => {
    setQuote(q);
  }, []);

  const openQuoteOptions = useCallback(() => {
    setActiveKey(QUOTE_OPTIONS_KEY);
  }, []);

  const handleRailSelect = useCallback(
    (key: string) => {
      if (activeKey === QUOTE_OPTIONS_KEY && key !== QUOTE_OPTIONS_KEY) {
        // Leaving Quote Options — a selection there changes the
        // recommendation figures + completeness server-side.
        void refreshSections();
        void refreshQuote();
      }
      setActiveKey(key);
    },
    [activeKey, refreshSections, refreshQuote],
  );

  // -- Rail model -----------------------------------------------------------

  const railEntries = useMemo<RailEntry[]>(() => {
    const bootstrapped = Boolean(proposal);
    // Until the backend sections exist (new mode, or the brief window right
    // after the bootstrap before the id-route load lands) show the standard
    // titles as locked placeholders.
    const backend: RailEntry[] = sections.length > 0
      ? sections.map((s) => ({
          key: s.sectionKey,
          title: s.title,
          complete: s.complete,
          locked: false,
        }))
      : config.newModeSections.map((s) => ({
          key: s.key,
          title: s.title,
          complete: false,
          locked: true,
        }));
    return [
      {
        key: QUOTE_REQUEST_KEY,
        title: 'Quote Request',
        complete: bootstrapped,
        locked: false,
      },
      {
        key: QUOTE_OPTIONS_KEY,
        title: 'Quote Options',
        complete: Boolean(quote?.selectedOptionId),
        locked: !bootstrapped,
      },
      ...backend,
    ];
  }, [proposal, sections, quote, config.newModeSections]);

  const availableToAdd = useMemo(
    () =>
      proposal
        ? ADDABLE_PROPOSAL_SECTION_KEYS.filter(
            (key) => !sections.some((s) => s.sectionKey === key),
          )
        : [],
    [proposal, sections],
  );

  const handleAddSection = useCallback(
    async (key: string) => {
      if (!proposalId) return;
      setAddMenuOpen(false);
      setAddingKey(key);
      try {
        const created = await proposalApi.addSection(proposalId, key);
        setSections((prev) => [...prev, created].sort((a, b) => a.sortOrder - b.sortOrder));
        setActiveKey(created.sectionKey);
      } catch {
        // Best-effort — the rail simply doesn't gain the section; the
        // advisor can retry from the "+" menu.
      } finally {
        setAddingKey(null);
      }
    },
    [proposalId],
  );

  const activeSection = useMemo(
    () => sections.find((s) => s.sectionKey === activeKey) ?? null,
    [sections, activeKey],
  );

  // Share gate (PRD §3.4): every loaded section must be complete except the
  // optional APPENDICES / boilerplate NEXT_STEPS.
  const shareable = useMemo(
    () =>
      sections.length > 0 &&
      sections.every((s) => SHARE_OPTIONAL_SECTION_KEYS.has(s.sectionKey) || s.complete),
    [sections],
  );

  const handleShare = useCallback(async () => {
    if (!proposalId || sharing) return;
    setSharing(true);
    try {
      await proposalApi.shareProposal(proposalId);
      setShareConfirmOpen(false);
      showToast('Proposal shared with the client', 'success');
      navigate(profileId ? `/relationships/${profileId}?tab=proposals` : '/relationships');
    } catch (err) {
      setShareConfirmOpen(false);
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const message = apiErrorMessage(err, 'Failed to share the proposal');
      if (status === 422 && message.startsWith('SECTIONS_INCOMPLETE')) {
        const keys = extractIncompleteSectionKeys(message, sections);
        const titles = keys.map(
          (key) => sections.find((s) => s.sectionKey === key)?.title ?? key,
        );
        if (keys.length > 0) setActiveKey(keys[0]);
        showToast(
          titles.length > 0
            ? `Complete these sections before sharing: ${titles.join(', ')}`
            : 'Complete all required sections before sharing.',
          'error',
        );
      } else if (status === 422 && message.startsWith('RECOMMENDATION_STALE')) {
        setActiveKey(config.recommendationSectionKey);
        showToast(
          `The quote selection changed — review and save the ${config.recommendationSectionTitle} section first.`,
          'error',
        );
      } else if (status === 409) {
        showToast('This proposal is no longer a draft — it may already have been sent.', 'error');
      } else {
        showToast(message, 'error');
      }
    } finally {
      setSharing(false);
    }
  }, [
    proposalId,
    sharing,
    showToast,
    navigate,
    profileId,
    sections,
    config.recommendationSectionKey,
    config.recommendationSectionTitle,
  ]);

  if (loading) {
    return (
      <div className="pension-builder" data-testid={`${config.testIdPrefix}-loading`}>
        <div className="pension-builder__card" style={{ display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      </div>
    );
  }

  if (error || (proposalId && !proposal)) {
    return (
      <div className="pension-builder">
        <div
          className="pension-builder__card pension-builder__error"
          data-testid={`${config.testIdPrefix}-error`}
        >
          {error ?? 'Failed to load this proposal'}
        </div>
      </div>
    );
  }

  return (
    <div className="pension-builder" data-testid={config.testIdPrefix}>
      {/* The toggle is always available (consistent with the Breakthrough
          builder). Before the proposal is bootstrapped there is no
          client-facing document yet, so the Client view shows a friendly
          empty state rather than being hidden. */}
      <div className="pension-builder__view-toggle-header">
        <ButtonGroup aria-label="Advisor view or client view" data-testid="quote-backed-builder-view-toggle">
          <Button
            variant={viewMode === 'advisor' ? 'filled' : 'outlined'}
            onClick={() => setViewMode('advisor')}
            aria-pressed={viewMode === 'advisor'}
            data-testid="quote-backed-builder-view-toggle-advisor"
          >
            Advisor view
          </Button>
          <Button
            variant={viewMode === 'client' ? 'filled' : 'outlined'}
            onClick={() => setViewMode('client')}
            aria-pressed={viewMode === 'client'}
            data-testid="quote-backed-builder-view-toggle-client"
          >
            Client view
          </Button>
        </ButtonGroup>
      </div>

      {viewMode === 'client' ? (
        <QuoteBackedClientPreview
          proposalId={proposalId ?? ''}
          profileId={profileId}
          recommendationSectionKey={config.recommendationSectionKey}
          sections={sections}
          onSaved={handleSectionSaved}
          onRemoved={handleSectionRemoved}
          onBackToEditing={() => setViewMode('advisor')}
        />
      ) : (
      <div className="pension-builder__body">
        <nav className="pension-builder__rail" aria-label="Proposal sections">
          {railEntries.map((entry) => (
            <button
              key={entry.key}
              type="button"
              className={[
                'pension-builder__rail-item',
                entry.key === activeKey ? 'pension-builder__rail-item--active' : '',
                entry.locked ? 'pension-builder__rail-item--locked' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleRailSelect(entry.key)}
              disabled={entry.locked}
              data-testid={`${config.testIdPrefix}-rail-item-${entry.key}`}
            >
              <span>{entry.title}</span>
              {entry.complete && (
                <span
                  className="pension-builder__rail-tick"
                  data-testid={`${config.testIdPrefix}-rail-tick-${entry.key}`}
                >
                  <AppIcon name="tick circle" size={16} color="green" />
                </span>
              )}
            </button>
          ))}

          {availableToAdd.length > 0 && (
            <div className="pension-builder__add-section">
              <IconButton
                variant="square-outlined"
                size="sm"
                onClick={() => setAddMenuOpen((v) => !v)}
                icon={<AppIcon name="add" />}
                aria-label="Add section"
                aria-haspopup="menu"
                aria-expanded={addMenuOpen}
                data-testid={`${config.testIdPrefix}-add-section-btn`}
              />
              {addMenuOpen && (
                <div
                  className="pension-builder__add-menu"
                  role="menu"
                  data-testid={`${config.testIdPrefix}-add-menu`}
                >
                  {availableToAdd.map((key) => (
                    <button
                      key={key}
                      type="button"
                      role="menuitem"
                      className="pension-builder__add-menu-item"
                      onClick={() => handleAddSection(key)}
                      disabled={addingKey === key}
                      data-testid={`${config.testIdPrefix}-add-menu-item-${key}`}
                    >
                      {ADDABLE_SECTION_LABELS[key] ?? key}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Share only exists once the proposal does ("new" mode hides it). */}
          {proposal && (
            <div className="pension-builder__rail-share">
              {shareable ? (
                <Button
                  variant="filled"
                  onClick={() => setShareConfirmOpen(true)}
                  disabled={sharing}
                  loading={sharing}
                  data-testid={`${config.testIdPrefix}-share-btn`}
                >
                  Share With Client
                </Button>
              ) : (
                <Tooltip
                  content="Complete all required sections first"
                  trigger={
                    <Button
                      variant="filled"
                      disabled
                      data-testid={`${config.testIdPrefix}-share-btn`}
                    >
                      Share With Client
                    </Button>
                  }
                />
              )}
            </div>
          )}
        </nav>

        <div className="pension-builder__content">
          {activeKey === QUOTE_REQUEST_KEY ? (
            proposal && !quote ? (
              // Edit mode needs the linked quote before the form can render
              // pre-filled — brief spinner while it loads.
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Spinner />
              </div>
            ) : proposalType === 'PENSION' ? (
              <WizardStepQuoteRequest
                profileId={profileId ?? ''}
                brokerageId={brokerageId}
                clients={clients}
                existingQuote={proposal ? quote ?? undefined : undefined}
                targetClientId={proposal?.targetClientId}
                onBootstrapped={handleBootstrapped}
                onQuoteUpdated={handleQuoteUpdated}
                onCancel={handleQuoteRequestCancel}
              />
            ) : proposalType === 'FUND_INVESTMENT' ? (
              <InvestmentQuoteRequestStep
                profileId={profileId ?? ''}
                brokerageId={brokerageId}
                clients={clients}
                existingQuote={proposal ? quote ?? undefined : undefined}
                targetClientId={proposal?.targetClientId}
                onBootstrapped={handleBootstrapped}
                onQuoteUpdated={handleQuoteUpdated}
                onCancel={handleQuoteRequestCancel}
              />
            ) : (
              <ProtectionQuoteRequestStep
                profileId={profileId ?? ''}
                brokerageId={brokerageId}
                clients={clients}
                existingQuote={proposal ? quote ?? undefined : undefined}
                targetClientId={proposal?.targetClientId}
                onBootstrapped={handleBootstrapped}
                onQuoteUpdated={handleQuoteUpdated}
                onCancel={handleQuoteRequestCancel}
              />
            )
          ) : activeKey === QUOTE_OPTIONS_KEY && proposal?.sourceQuoteId ? (
            <WizardStepQuoteOptions
              key={`${proposal.sourceQuoteId}-${optionsRefreshKey}`}
              quoteId={proposal.sourceQuoteId}
              onEditRequested={handleEditRequested}
              onSaveAndClose={handleSaveAndClose}
              onCreateStatement={handleContinueToBuilder}
              createStatementLabel="Continue to Builder"
              onQuoteChanged={handleQuoteChanged}
            />
          ) : (
            activeSection && (
              <>
                <h2 className="pension-builder__section-title">{activeSection.title}</h2>
                {renderQuoteBackedSection(
                  activeSection,
                  proposalId ?? '',
                  profileId,
                  handleSectionSaved,
                  handleSectionRemoved,
                  openQuoteOptions,
                  config.recommendationSectionKey,
                )}
              </>
            )
          )}
        </div>
      </div>
      )}

      <Modal open={shareConfirmOpen} onClose={() => setShareConfirmOpen(false)} size="medium">
        <Modal.Header
          icon="send"
          title="Share With Client"
          onClose={() => setShareConfirmOpen(false)}
        />
        <Modal.Content>
          <p data-testid={`${config.testIdPrefix}-share-copy`}>
            This freezes the plan, generates the Statement of Suitability and sends it to the
            client.
          </p>
        </Modal.Content>
        <Modal.Actions
          secondaryAction={{
            label: 'Cancel',
            onClick: () => setShareConfirmOpen(false),
            disabled: sharing,
          }}
          primaryAction={{
            label: 'Share With Client',
            onClick: () => {
              void handleShare();
            },
            disabled: sharing,
            loading: sharing,
          }}
        />
      </Modal>
    </div>
  );
}

export default QuoteBackedProposalBuilder;
